import React, { Context, ReactNode } from 'react'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { TabContextProps } from './tab-context'

export const tabHeadingsRightPadding = 40

export interface TabHeadingConfig<TTabs extends string> {
  readonly id: TTabs
  readonly content: (props: {
    readonly isActive: boolean
  }) => React.ReactElement
  readonly hide?: boolean
  readonly onInactiveTabClick?: () => void
}

interface TabHeadingsProps<TTabs extends string> {
  readonly renderExtra?: ReactNode
  readonly showBackgroundColor?: boolean
  readonly context: Context<TabContextProps<TTabs>>
  readonly tabHeadingConfigs: Array<TabHeadingConfig<TTabs>>
}

/**
 * Renders the main tab headings.
 * Depends on TabContext
 */
export function TabHeadings<TTabs extends string>({
  renderExtra,
  context: TabContext,
  tabHeadingConfigs,
  showBackgroundColor = false
}: TabHeadingsProps<TTabs>) {
  return (
    <TabContext.Consumer>
      {({
        transitionToggleFlag,
        updateToggleFlag,
        tabHeadingWidths,
        updateTabHeadingWidths,
        currentTabID: currID,
        setCurrentTabID
      }) => {
        return (
          <ul
            className={cx(
              css`
                padding: 0 0 5px 0;
                margin: 0;
                display: flex;
                & li {
                  list-style-type: none;
                }
              `,
              showBackgroundColor
                ? css`
                    background-color: ${theme.color.white};
                  `
                : undefined
            )}
          >
            {tabHeadingConfigs.map(
              (
                { id, content: TabHeadingContent, onInactiveTabClick },
                tabIndex
              ) => {
                const isActive = id === currID
                return (
                  <li
                    data-testid={`tab-heading-${id}`}
                    key={tabIndex}
                    className={cx(
                      css`
                        fontfamily: ${deprecatedTheme.fontFamilies
                          .primaryCondensed};
                        fontsize: 13px;
                        fontweight: 400;
                        texttransform: uppercase;
                        color: ${theme.color.neutral50};
                        display: block;
                        padding: 0;
                        padding-right: ${tabHeadingsRightPadding}px;
                        color: ${isActive ? theme.color.neutral100 : 'initial'};
                        ${!isActive && 'cursor: pointer;'};
                      `
                    )}
                    ref={(ref) => {
                      if (ref !== null) {
                        const width = ref.offsetWidth - tabHeadingsRightPadding
                        if (
                          !tabHeadingWidths[id] ||
                          tabHeadingWidths[id] !== width
                        ) {
                          updateTabHeadingWidths({
                            ...tabHeadingWidths,
                            [id]: width
                          })
                        }
                      }
                    }}
                    onClick={() => {
                      if (id !== currID) {
                        setCurrentTabID(id)
                        updateToggleFlag(!transitionToggleFlag)
                      }
                      if (onInactiveTabClick && !isActive) {
                        onInactiveTabClick()
                      }
                    }}
                  >
                    <TabHeadingContent isActive={isActive} />
                  </li>
                )
              }
            )}
            {!!renderExtra && (
              <li
                className={css`
                  margin-left: auto;
                `}
              >
                {renderExtra}
              </li>
            )}
          </ul>
        )
      }}
    </TabContext.Consumer>
  )
}
